/**
 * <cba-tier-bar> — the reward bar (snippets/cba-tier-bar): the labels per
 * step and the celebration when a step is reached.
 *
 *   <cba-tier-bar id="cba-tier-bar-header" class="cba-cart-progress"
 *     data-eligible-count="2" data-has-bundle="false">
 *     … [data-cba-tier-bar-seg data-tier-key data-tier-min data-tier-max
 *        data-tier-reward data-tier-reward-bundle]
 *     … [data-cba-tier-bar-complete]
 *   </cba-tier-bar>
 *
 * - It shows the count the server computed (data-eligible-count; /cart.js
 *   cannot see product tags or line-level prices, so the script never
 *   counts).
 * - After each cart change (Shopify's standard cart events, through
 *   @theme/cart) a bar outside the cart reads its new count from its own
 *   section in the change's answer, or loads the section when the change
 *   brought none. The bar in the cart is re-rendered with the cart
 *   (<cba-cart-items>): the new element starts itself.
 * - What a bar last showed is remembered per id for the page's life, so a
 *   bar the cart replaced celebrates only a step that was really just
 *   reached.
 * - An ES module, loaded once from the layout (it was an IIFE with globals,
 *   its <script> printed with every bar, three a page; DESIGN-PLAN 7.G6,
 *   shell audit S1.8, S18.2).
 */
import { onCartUpdate, loadSection } from '@theme/cart';

if (!customElements.get('cba-tier-bar')) {
  /* Per bar id: { prevDoneByKey, prevComplete, firstRenderDone }. */
  const memory = new Map();

  /* A step's label from its state. The step being worked on shows the
     "N more = <reward>" prompt; reached and locked steps show the reward. */
  const segLabel = (reward, max, qty, isActive) => {
    if (qty >= max || !isActive) return reward;
    return (max - qty) + ' more = ' + reward.charAt(0).toLowerCase() + reward.slice(1);
  };

  class CbaTierBar extends HTMLElement {
    connectedCallback() {
      this.segs = Array.from(this.querySelectorAll('[data-cba-tier-bar-seg]'));
      this.completeEl = this.querySelector('[data-cba-tier-bar-complete]');
      if (!this.segs.length) return;
      const key = this.id || 'cba-tier-bar';
      this.mem = memory.get(key) || { prevDoneByKey: {}, prevComplete: false, firstRenderDone: false };
      memory.set(key, this.mem);
      this.abort = new AbortController();
      onCartUpdate((result) => this.refresh(result && result.detail ? result.detail.sections : null), { signal: this.abort.signal });
      this.renderFromDom();
    }

    disconnectedCallback() {
      if (this.abort) this.abort.abort();
    }

    renderFromDom() {
      this.render(parseInt(this.getAttribute('data-eligible-count'), 10) || 0);
    }

    render(qty) {
      const mem = this.mem;
      const complete = qty >= 3;
      const hasBundle = this.getAttribute('data-has-bundle') === 'true';
      this.classList.toggle('cba-cart-progress--complete', complete);

      let activeAssigned = false;
      this.segs.forEach((seg) => {
        const key = seg.getAttribute('data-tier-key');
        const min = parseInt(seg.getAttribute('data-tier-min'), 10);
        const max = parseInt(seg.getAttribute('data-tier-max'), 10);
        const reward = seg.getAttribute('data-tier-reward') || '';
        const rewardBundle = seg.getAttribute('data-tier-reward-bundle');
        const fill = seg.querySelector('.cba-cart-progress__fill');
        const labelNode = seg.querySelector('[data-cba-tier-bar-reward]');
        const prevDone = mem.prevDoneByKey[key] === true;

        const done = qty >= max;
        let isActive = false;
        if (!done && !activeAssigned) { isActive = true; activeAssigned = true; }

        seg.classList.toggle('cba-cart-progress__seg--done', done);
        seg.classList.toggle('cba-cart-progress__seg--active', isActive);

        let bar = 0;
        if (done) bar = 100;
        else if (isActive && max > min) bar = Math.max(0, Math.round((qty - min) * 100 / (max - min)));
        if (fill) fill.style.setProperty('--cba-tier-bar-fill', bar + '%');
        const effReward = (hasBundle && rewardBundle && !done) ? rewardBundle : reward;
        if (labelNode) labelNode.textContent = segLabel(effReward, max, qty, isActive);

        /* Celebrate a real unlock only while the track still shows (once
           complete, the celebration bar does it instead). */
        if (mem.firstRenderDone && done && !prevDone && !complete) this.replay(seg, 'cba-cart-progress__seg--just-unlocked');
        else if (!done) seg.classList.remove('cba-cart-progress__seg--just-unlocked');

        mem.prevDoneByKey[key] = done;
      });

      /* The top step: celebrate on the change from not complete to complete. */
      if (this.completeEl && mem.firstRenderDone && complete && !mem.prevComplete) this.replay(this.completeEl, 'cba-cart-progress__complete--just');
      mem.prevComplete = complete;
      mem.firstRenderDone = true;
    }

    /* Start a CSS animation again: off, a reflow, on. */
    replay(el, className) {
      el.classList.remove(className);
      void el.offsetWidth;
      el.classList.add(className);
    }

    /* A bar outside the cart reads its count from a fresh render of its own
       section: the change's answer carries it, or one shared request loads it. */
    refresh(sections) {
      if (this.closest('cba-cart-items')) return; /* re-rendered with the cart */
      const section = this.closest('.shopify-section');
      const sectionId = section && section.id ? section.id.replace(/^shopify-section-/, '') : null;
      if (!sectionId) return;
      const html = sections && sections[sectionId];
      (html ? Promise.resolve(html) : loadSection(sectionId))
        .then((fresh) => {
          if (!fresh || !this.isConnected) return;
          const next = new DOMParser().parseFromString(fresh, 'text/html').getElementById(this.id || 'cba-tier-bar');
          if (!next) return;
          this.setAttribute('data-eligible-count', next.getAttribute('data-eligible-count') || '0');
          this.setAttribute('data-has-bundle', next.getAttribute('data-has-bundle') || 'false');
          this.renderFromDom();
        })
        .catch(() => { /* keep what it shows */ });
    }
  }

  customElements.define('cba-tier-bar', CbaTierBar);
}
